import React, { FC, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Document, Page, pdfjs } from 'react-pdf';
import { Result } from 'antd';
import { CloseOutlined } from '@ant-design/icons';

import styles from './StudyFile.module.scss';
import Loader from '../Loader';
import useStore from '../../hooks/useStore';
import { FilesApi } from '../../api/files';
import { getAuthToken } from '../../utils/tokenHelpers';
import { isMobile } from 'react-device-detect';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const StudyFile: FC = () => {
  const params = useParams<{ studyUUID: string; fileUUID: string }>();
  const history = useHistory();
  const location = useLocation();
  const [fileUrl, setFileUrl] = useState<string>('');
  const [numPages, setNumPages] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [containerWidth, setContainerWidth] = useState<number>(
    window.innerWidth
  );

  const { cashFiles } = useStore();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (!params.studyUUID || !params.fileUUID) return;
        setError('');
        setLoading(true);
        document.body.style.overflow = 'hidden';

        const file = cashFiles.getFile(params.fileUUID);
        if (file) {
          setFileUrl(file.blobUrl);
          return;
        }

        const { url, filename } = await FilesApi.getFileById(
          params.studyUUID,
          params.fileUUID
        );
        cashFiles.addFile({
          serverUrl: params.fileUUID,
          blobUrl: url,
          name: filename,
        });
        setFileUrl(url);
      } catch (e: any) {
        setError(
          e.message || 'Файл недоступний, зверніться до діагностичного центру'
        );
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      document.body.style.overflow = '';
    };
  }, [params]);

  const handleClose = () => {
    if (location.pathname.includes('/sharedStudies')) {
      history.push(`/sharedStudies?token=${getAuthToken().token}`);
    } else {
      history.push('/');
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  return (
    <div className={styles.studyFile}>
      <button className={styles.close} onClick={handleClose}>
        <CloseOutlined rev />
      </button>

      {loading ? (
        <div className={styles.studyFile}>
          <Loader />
        </div>
      ) : error ? (
        <div className={styles.errorBlock}>
          <Result status="error" subTitle={error} />
        </div>
      ) : fileUrl ? (
        <div ref={containerRef} className={styles.pdfScrollContainer}>
          <Document
            file={fileUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={<Loader />}
            error={
              <Result status="error" subTitle="Помилка при завантаженні PDF" />
            }
          >
            {Array.from(new Array(numPages), (_, index) => (
              <Page
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                width={isMobile ? containerWidth : 800}
              />
            ))}
          </Document>
        </div>
      ) : null}
    </div>
  );
};

export default observer(StudyFile);
